import React from 'react';
import useThemeCanvas, { hexToRgb } from '../useThemeCanvas';

// Fireflies — a dusk meadow with depth: a warm afterglow on the horizon,
// distant out-of-focus firefly bokeh, swaying grass in the foreground, and
// blinking fireflies that drift on lazy organic curves leaving a faint trail.

const NEAR_COUNT = 46;
const BOKEH_COUNT = 14;
const FIREFLY_RGB = { r: 222, g: 240, b: 132 }; // warm yellow-green

function spawnFly(w, h) {
  const depth = Math.random(); // 0 far .. 1 near
  return {
    x: Math.random() * w,
    y: Math.random() * h * 0.95,
    vx: (Math.random() - 0.5) * 0.25,
    vy: (Math.random() - 0.5) * 0.2,
    ax: 0, ay: 0,
    depth,
    r: 1 + depth * 2,
    blinkFreq: 0.5 + Math.random() * 0.9,
    blinkPhase: Math.random() * Math.PI * 2,
    palettePick: Math.random(),
    trail: [],
  };
}

export default function FirefliesTheme({ palette, colorMode, dark }) {
  const canvasRef = useThemeCanvas({
    init: (ctx, w, h, state) => {
      state.flies = Array.from({ length: NEAR_COUNT }, () => spawnFly(w, h));
      state.bokeh = Array.from({ length: BOKEH_COUNT }, () => ({
        x: Math.random() * w,
        y: Math.random() * h * 0.7,
        r: 30 + Math.random() * 60,
        drift: 0.05 + Math.random() * 0.12,
        phase: Math.random() * Math.PI * 2,
      }));
      // Three grass layers: far (short, faint) → near (tall, dark).
      state.grass = [0, 1, 2].map((layer) => {
        const blades = [];
        const density = 90 - layer * 18;
        for (let i = 0; i < density; i++) {
          blades.push({
            x: Math.random() * w,
            hgt: (20 + Math.random() * 40) * (0.5 + layer * 0.4),
            wid: 3 + Math.random() * 4 + layer,
            phase: Math.random() * Math.PI * 2,
            lean: (Math.random() - 0.5) * 0.4,
          });
        }
        return blades;
      });
    },
    draw: (ctx, w, h, state, t) => {
      // Dusk sky
      const sky = ctx.createLinearGradient(0, 0, 0, h);
      if (dark) {
        sky.addColorStop(0, '#0a0f22');
        sky.addColorStop(0.5, '#1a1840');
        sky.addColorStop(0.82, '#3a1f4a');
        sky.addColorStop(1, '#5a2f3a');
      } else {
        sky.addColorStop(0, '#28456f');
        sky.addColorStop(0.5, '#7c4589');
        sky.addColorStop(0.85, '#c2724e');
        sky.addColorStop(1, '#e0a063');
      }
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, w, h);

      // Warm afterglow rising from the horizon.
      const glow = ctx.createRadialGradient(w * 0.5, h * 1.02, 0, w * 0.5, h * 1.02, h * 0.8);
      glow.addColorStop(0, dark ? 'rgba(220,140,90,0.32)' : 'rgba(255,210,150,0.5)');
      glow.addColorStop(1, 'rgba(220,140,90,0)');
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, w, h);

      // Distant out-of-focus firefly bokeh.
      ctx.globalCompositeOperation = 'screen';
      for (const o of state.bokeh) {
        o.x += o.drift;
        if (o.x > w + o.r) o.x = -o.r;
        const pulse = 0.5 + 0.5 * Math.sin(t * 0.6 + o.phase);
        const a = (dark ? 0.1 : 0.14) * pulse;
        const rg = ctx.createRadialGradient(o.x, o.y, 0, o.x, o.y, o.r);
        rg.addColorStop(0, `rgba(${FIREFLY_RGB.r},${FIREFLY_RGB.g},${FIREFLY_RGB.b},${a})`);
        rg.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = rg;
        ctx.beginPath();
        ctx.arc(o.x, o.y, o.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Fireflies (drawn additively for glow bloom).
      for (const f of state.flies) {
        f.ax += (Math.random() - 0.5) * 0.02;
        f.ay += (Math.random() - 0.5) * 0.02;
        f.ax *= 0.92; f.ay *= 0.92;
        f.vx = (f.vx + f.ax) * 0.96;
        f.vy = (f.vy + f.ay) * 0.96;
        const sp = Math.hypot(f.vx, f.vy);
        const max = 0.4 + f.depth * 0.5;
        if (sp > max) { f.vx *= max / sp; f.vy *= max / sp; }
        f.x += f.vx; f.y += f.vy;
        if (f.x < -10) f.x = w + 10;
        if (f.x > w + 10) f.x = -10;
        if (f.y < -10) f.y = h + 10;
        if (f.y > h + 10) f.y = -10;

        f.trail.push([f.x, f.y]);
        if (f.trail.length > 6) f.trail.shift();

        // Blink: mostly dim with brief bright flashes.
        const raw = 0.5 + 0.5 * Math.sin(t * f.blinkFreq + f.blinkPhase);
        const intensity = 0.12 + 0.88 * Math.pow(raw, 3);

        let rgb = FIREFLY_RGB;
        if (colorMode === 'cycling') {
          rgb = hexToRgb(palette[Math.floor(t * 0.3 + f.palettePick * palette.length) % palette.length]);
        } else if (colorMode === 'vibrant') {
          rgb = hexToRgb(palette[Math.floor(f.palettePick * palette.length)]);
        }

        // Faint trail
        for (let i = 0; i < f.trail.length - 1; i++) {
          const [tx, ty] = f.trail[i];
          const ta = (i / f.trail.length) * intensity * 0.25;
          ctx.fillStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},${ta})`;
          ctx.beginPath();
          ctx.arc(tx, ty, f.r * 0.6, 0, Math.PI * 2);
          ctx.fill();
        }

        // Glow halo
        const glowR = f.r * (5 + intensity * 5);
        const g2 = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, glowR);
        g2.addColorStop(0, `rgba(${rgb.r},${rgb.g},${rgb.b},${0.6 * intensity})`);
        g2.addColorStop(0.4, `rgba(${rgb.r},${rgb.g},${rgb.b},${0.16 * intensity})`);
        g2.addColorStop(1, `rgba(${rgb.r},${rgb.g},${rgb.b},0)`);
        ctx.fillStyle = g2;
        ctx.beginPath();
        ctx.arc(f.x, f.y, glowR, 0, Math.PI * 2);
        ctx.fill();

        // Bright core
        ctx.fillStyle = `rgba(255,255,${Math.min(255, rgb.b + 80)},${0.9 * intensity + 0.1})`;
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalCompositeOperation = 'source-over';

      // Foreground grass (far → near), swaying.
      state.grass.forEach((blades, layer) => {
        const baseY = h - layer * 4;
        const tone = dark
          ? `rgba(${6 + layer * 4},${12 + layer * 8},${10 + layer * 6},0.92)`
          : `rgba(${18 + layer * 10},${30 + layer * 12},${24 + layer * 8},0.85)`;
        ctx.strokeStyle = tone;
        ctx.lineCap = 'round';
        for (const bl of blades) {
          const sway = Math.sin(t * 0.7 + bl.phase) * (4 + layer * 3) + bl.lean * bl.hgt;
          ctx.lineWidth = bl.wid;
          ctx.beginPath();
          ctx.moveTo(bl.x, baseY);
          ctx.quadraticCurveTo(bl.x + sway * 0.5, baseY - bl.hgt * 0.6, bl.x + sway, baseY - bl.hgt);
          ctx.stroke();
        }
      });
      ctx.lineCap = 'butt';
    },
  });
  return <canvas ref={canvasRef} className="absolute inset-0" />;
}
